/**
 * Core Web Vitals Tracking
 * 
 * Tracks and reports LCP (Largest Contentful Paint), INP (Interaction to Next Paint),
 * and CLS (Cumulative Layout Shift) metrics.
 * 
 * Uses the web-vitals library approach for accurate measurement.
 * Sends metrics to Google Tag Manager dataLayer for analytics integration.
 */

(function() {
  'use strict';

  // Check if dataLayer exists (from GTM)
  if (typeof window.dataLayer === 'undefined') {
    window.dataLayer = [];
  }

  /**
   * Report metric to dataLayer for GTM/GA4
   */
  function reportMetric(metricName, value, id, delta, rating) {
    window.dataLayer.push({
      event: 'web_vitals',
      web_vitals: {
        metric_name: metricName,
        value: Math.round(value),
        id: id,
        delta: Math.round(delta),
        rating: rating // 'good', 'needs-improvement', or 'poor'
      }
    });

    // Also log to console in development
    if (window.location.hostname === 'localhost' || window.location.hostname.includes('127.0.0.1')) {
      console.log('[Core Web Vitals]', metricName, {
        value: Math.round(value),
        rating: rating
      });
    }
  }

  /**
   * Get rating for a metric value
   */
  function getRating(metricName, value) {
    const thresholds = {
      LCP: { good: 2500, poor: 4000 },
      INP: { good: 200, poor: 500 },
      CLS: { good: 0.1, poor: 0.25 },
      FID: { good: 100, poor: 300 } // Legacy, but included for completeness
    };

    const threshold = thresholds[metricName];
    if (!threshold) return 'unknown';

    if (value <= threshold.good) return 'good';
    if (value >= threshold.poor) return 'poor';
    return 'needs-improvement';
  }

  /**
   * Track Largest Contentful Paint (LCP)
   */
  function trackLCP() {
    try {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        
        // LCP is the last entry
        const lcpValue = lastEntry.renderTime || lastEntry.loadTime;
        
        reportMetric(
          'LCP',
          lcpValue,
          lastEntry.id,
          lcpValue,
          getRating('LCP', lcpValue)
        );
      });

      observer.observe({ entryTypes: ['largest-contentful-paint'] });
    } catch (e) {
      // PerformanceObserver not supported
      if (window.location.hostname === 'localhost' || window.location.hostname.includes('127.0.0.1')) {
        console.warn('[Core Web Vitals] LCP tracking not supported:', e);
      }
    }
  }

  /**
   * Track Interaction to Next Paint (INP)
   * Falls back to First Input Delay (FID) for older browsers
   */
  function trackINP() {
    try {
      // Try INP first (newer metric)
      if ('PerformanceEventTiming' in window) {
        const observer = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          
          // Find the worst INP (highest value)
          let worstINP = { value: 0, id: null };
          
          entries.forEach((entry) => {
            if (entry.interactionId) {
              const inpValue = entry.processingStart - entry.startTime;
              if (inpValue > worstINP.value) {
                worstINP = {
                  value: inpValue,
                  id: entry.interactionId
                };
              }
            }
          });

          if (worstINP.value > 0) {
            reportMetric(
              'INP',
              worstINP.value,
              worstINP.id,
              worstINP.value,
              getRating('INP', worstINP.value)
            );
          }
        });

        observer.observe({ entryTypes: ['event'] });
      } else {
        // Fallback to FID for older browsers
        trackFID();
      }
    } catch (e) {
      // Fallback to FID
      trackFID();
    }
  }

  /**
   * Track First Input Delay (FID) - legacy metric, fallback for INP
   */
  function trackFID() {
    try {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const firstInput = entries[0];
        
        if (firstInput) {
          const fidValue = firstInput.processingStart - firstInput.startTime;
          
          reportMetric(
            'FID',
            fidValue,
            firstInput.name,
            fidValue,
            getRating('FID', fidValue)
          );
        }
      });

      observer.observe({ entryTypes: ['first-input'] });
    } catch (e) {
      // PerformanceObserver not supported
      if (window.location.hostname === 'localhost' || window.location.hostname.includes('127.0.0.1')) {
        console.warn('[Core Web Vitals] FID tracking not supported:', e);
      }
    }
  }

  /**
   * Track Cumulative Layout Shift (CLS)
   */
  function trackCLS() {
    try {
      let clsValue = 0;
      let clsEntries = [];

      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        
        entries.forEach((entry) => {
          // Only count layout shifts without recent user input
          if (!entry.hadRecentInput) {
            clsEntries.push(entry);
            clsValue += entry.value;
          }
        });

        // Report CLS when page is about to unload or after a delay
        if (document.visibilityState === 'hidden') {
          reportMetric(
            'CLS',
            clsValue,
            'cumulative',
            clsValue,
            getRating('CLS', clsValue)
          );
        }
      });

      observer.observe({ entryTypes: ['layout-shift'] });

      // Report CLS when page is hidden (user navigates away)
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden' && clsValue > 0) {
          reportMetric(
            'CLS',
            clsValue,
            'cumulative',
            clsValue,
            getRating('CLS', clsValue)
          );
        }
      });

      // Also report CLS after a delay to catch early shifts
      setTimeout(() => {
        if (clsValue > 0) {
          reportMetric(
            'CLS',
            clsValue,
            'cumulative',
            clsValue,
            getRating('CLS', clsValue)
          );
        }
      }, 5000);
    } catch (e) {
      // PerformanceObserver not supported
      if (window.location.hostname === 'localhost' || window.location.hostname.includes('127.0.0.1')) {
        console.warn('[Core Web Vitals] CLS tracking not supported:', e);
      }
    }
  }

  // Initialize tracking when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      trackLCP();
      trackINP();
      trackCLS();
    });
  } else {
    // DOM already loaded
    trackLCP();
    trackINP();
    trackCLS();
  }
})();




